import { FileArrowUpIcon } from "@phosphor-icons/react/dist/csr/FileArrowUp";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { FileUploadList } from "./file-upload-list";
import {
  formatRejectionMessage,
  validateFiles,
  type FileUploadRejection,
} from "./validate-files";

interface FileUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  description?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  fileTypeName?: string;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  multiple?: boolean;
  onReject?: (rejections: FileUploadRejection[]) => void;
  renderFooter?: React.ReactNode;
  showRemove?: boolean;
  asChild?: boolean;
}

const getAcceptAttribute = (accept?: string) => accept;

export const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      value,
      onChange,
      accept,
      description,
      disabled = false,
      errorMessage: errorMessageOverride,
      fileTypeName,
      maxFiles,
      maxSize,
      minSize,
      multiple = false,
      onReject,
      renderFooter,
      showRemove = true,
      asChild = false,
      className,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [dragCounter, setDragCounter] = React.useState(0);
    const [isDragReject, setIsDragReject] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const isDragActive = dragCounter > 0;
    const acceptAttr = React.useMemo(
      () => getAcceptAttribute(accept),
      [accept],
    );

    const validate = React.useCallback(
      (files: File[]) =>
        validateFiles(files, {
          accept,
          disabled,
          fileTypeName,
          maxFiles,
          maxSize,
          minSize,
        }),
      [accept, disabled, fileTypeName, maxFiles, maxSize, minSize],
    );

    const handleFiles = React.useCallback(
      (files: File[]) => {
        const { accepted, rejected } = validate(files);

        if (rejected.length > 0) {
          setIsDragReject(true);
          setErrorMessage(formatRejectionMessage(rejected));
          onReject?.(rejected);
        } else {
          setIsDragReject(false);
          setErrorMessage(null);
        }

        if (accepted.length === 0) return;

        const selectedFiles = multiple ? accepted : accepted.slice(0, 1);
        onChange(multiple ? [...value, ...selectedFiles] : selectedFiles);
      },
      [multiple, onChange, onReject, validate, value],
    );

    const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setIsDragReject(false);
      setErrorMessage(null);
      setDragCounter((prev) => prev + 1);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setDragCounter((prev) => Math.max(0, prev - 1));
    };

    const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setDragCounter(0);
      const files = Array.from(event.dataTransfer.files);
      handleFiles(files);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      handleFiles(files);
      event.target.value = "";
    };

    const open = () => {
      if (disabled) return;
      inputRef.current?.click();
    };

    const resolvedErrorMessage = errorMessageOverride ?? errorMessage;
    const hasFiles = value.length > 0;
    const hasError = Boolean(resolvedErrorMessage);
    const showSuccess = hasFiles && !hasError;
    const label = multiple
      ? hasFiles
        ? "Select another"
        : "Choose files"
      : hasFiles
        ? "Change file"
        : "Choose file";
    const fileLabel = multiple
      ? hasFiles
        ? `${value.length} file${value.length === 1 ? "" : "s"} selected`
        : "No files selected"
      : (value[0]?.name ?? "No file selected");
    const typeLabel = fileTypeName ? `a ${fileTypeName} file` : "a file";
    const state =
      isDragReject || Boolean(errorMessageOverride)
        ? "reject"
        : isDragActive
          ? "active"
          : "idle";
    const content = (
      <>
        <Stack
          align="start"
          direction="column"
          gap={0}
          data-state={state}
          data-disabled={disabled ? "true" : undefined}
          className={cn(
            "w-full min-w-[320px] rounded-lg border-2 border-dashed p-6 transition-colors",
            "data-[state=active]:border-brand-line data-[state=active]:bg-brand-bold/5",
            "data-[state=reject]:border-danger-line data-[state=reject]:bg-danger-subtle",
            "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60",
            showSuccess && "border-success-line bg-success-subtle",
          )}
          onClick={() => {
            if (!disabled) open();
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Stack className="w-full" gap={4} align="center">
            <FileArrowUpIcon
              className={cn(
                "h-12 w-12 text-subtle",
                showSuccess && "text-success",
              )}
            />
            <Stack className="w-full" gap={2} align="center">
              <Text className="max-w-full text-center break-all">
                {fileLabel}
              </Text>
              <Text className="text-center text-subtle">
                {description ??
                  (isDragActive
                    ? "Drop your file here"
                    : `Drag and drop ${typeLabel} here, or`)}
              </Text>
              {resolvedErrorMessage && (
                <Text
                  tone="destructive"
                  className="max-w-full text-center break-all"
                >
                  {resolvedErrorMessage}
                </Text>
              )}
              <Button
                variant="link"
                size="xs"
                onClick={(event) => {
                  event.stopPropagation();
                  open();
                }}
                disabled={disabled}
              >
                {label}
              </Button>
            </Stack>
          </Stack>
          {multiple && hasFiles && (
            <FileUploadList
              files={value}
              showRemove={showRemove}
              onChange={onChange}
            />
          )}
        </Stack>
        {/* Opened by the button above, so it is no tab stop of its own. */}
        <input
          ref={inputRef}
          type="file"
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          accept={acceptAttr}
          disabled={disabled}
          multiple={multiple}
          onChange={handleInputChange}
        />
        {renderFooter}
      </>
    );

    if (asChild) {
      return (
        <Stack
          ref={ref}
          gap={6}
          align="start"
          {...props}
          className={cn("w-full", className)}
        >
          {content}
        </Stack>
      );
    }

    return (
      <div ref={ref} className={cn(className)} {...props}>
        <Stack className="w-full" align="start" gap={6}>
          {content}
        </Stack>
      </div>
    );
  },
);

FileUpload.displayName = "FileUpload";

export type { FileUploadProps };
